/**
 * Shared browser cookie / Web Storage cleanup helpers.
 *
 * Consent teardown paths (Amplitude, Snapchat, …) use these to remove a service's own cookies and storage entries when consent is withdrawn. Each service supplies its own match predicate so the key sets stay disjoint between services.
 */

/** Expires a cookie across the host/domain variants a browser may have scoped it to. */
export const removeBrowserCookie = (name: string): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const { hostname, protocol } = window.location;
  const hostnameWithoutWww = hostname.replace(/^www\./, '');
  const domains = Array.from(
    new Set(['', hostname, `.${hostname}`, hostnameWithoutWww, `.${hostnameWithoutWww}`])
  );
  const secureAttribute = protocol === 'https:' ? '; Secure' : '';

  domains.forEach((domain) => {
    const domainAttribute = domain ? `; domain=${domain}` : '';
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax${domainAttribute}${secureAttribute}`;
  });
};

/** Removes every cookie whose name matches `shouldRemove`. Returns the number removed. */
export const clearBrowserCookies = (shouldRemove: (name: string) => boolean): number => {
  if (typeof document === 'undefined') {
    return 0;
  }

  let removed = 0;
  document.cookie.split(';').forEach((cookie) => {
    const [name] = cookie.trim().split('=');
    if (name && shouldRemove(name)) {
      removeBrowserCookie(name);
      removed += 1;
    }
  });

  return removed;
};

/**
 * Removes every key matching `shouldRemove` from the storage returned by `getStorage`.
 * Returns the number removed.
 *
 * `getStorage` is a thunk rather than a `Storage` instance so the `window.localStorage` /
 * `window.sessionStorage` access happens inside the try/catch: that property getter itself can throw (e.g. sandboxed iframes or when storage is blocked), which would otherwise break callers on the consent teardown path.
 */
export const clearWebStorage = (
  getStorage: () => Storage,
  shouldRemove: (key: string) => boolean
): number => {
  let removed = 0;

  try {
    const storage = getStorage();
    Object.keys(storage).forEach((key) => {
      if (shouldRemove(key)) {
        storage.removeItem(key);
        removed += 1;
      }
    });
  } catch {
    // Storage may be unavailable or throw on access in restricted browser contexts.
  }

  return removed;
};
